<script setup>
import { ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import AssistantAPI from 'dashboard/api/assistant';
import Button from 'dashboard/components-next/button/Button.vue';
import AssistantSection from './AssistantSection.vue';
import AssistantLoadError from './AssistantLoadError.vue';

const { t } = useI18n();

const images = ref([]);
const isFetching = ref(true);
const isUploading = ref(false);
const description = ref('');
const file = ref(null);
const fileInput = ref(null);
const deletingId = ref(null);
const loadError = ref(false);

const MAX_BYTES = 5 * 1024 * 1024;

async function load() {
  loadError.value = false;
  isFetching.value = true;
  try {
    const { data } = await AssistantAPI.getImages();
    images.value = data;
  } catch {
    loadError.value = true;
  } finally {
    isFetching.value = false;
  }
}

function onFileChange(event) {
  const [picked] = event.target.files || [];
  if (!picked) return;
  if (picked.size > MAX_BYTES) {
    useAlert(t('ASSISTANT.PICTURES.TOO_BIG'));
    event.target.value = '';
    // Drop the earlier pick too, or the rejected choice silently uploads
    // whatever was chosen before it under the new description.
    file.value = null;
    return;
  }
  file.value = picked;
}

async function upload() {
  if (isUploading.value) return;
  if (!file.value || !description.value.trim()) return;
  isUploading.value = true;
  try {
    const { data } = await AssistantAPI.createImage({
      description: description.value.trim(),
      file: file.value,
    });
    images.value = [...images.value, data];
    description.value = '';
    file.value = null;
    if (fileInput.value) fileInput.value.value = '';
    useAlert(t('ASSISTANT.PICTURES.ADDED'));
  } catch (error) {
    useAlert(
      error?.response?.data?.error || t('ASSISTANT.PICTURES.SAVE_ERROR')
    );
  } finally {
    isUploading.value = false;
  }
}

async function remove(image) {
  deletingId.value = image.id;
  try {
    await AssistantAPI.deleteImage(image.id);
    images.value = images.value.filter(i => i.id !== image.id);
  } catch {
    useAlert(t('ASSISTANT.PICTURES.DELETE_ERROR'));
  } finally {
    deletingId.value = null;
  }
}

onMounted(load);
</script>

<template>
  <AssistantSection
    :heading="t('ASSISTANT.PICTURES.TITLE')"
    :description="t('ASSISTANT.PICTURES.DESCRIPTION')"
    :count="isFetching || loadError ? null : images.length"
    icon="i-lucide-images"
  >
    <div class="flex min-w-0 flex-col gap-3 border-b border-n-weak p-4 sm:px-5">
      <label class="text-sm font-medium text-n-slate-12">
        {{ $t('ASSISTANT.PICTURES.DESCRIPTION_LABEL') }}
        <input
          v-model="description"
          type="text"
          maxlength="200"
          class="mt-2 h-10 w-full min-w-0 rounded-md border border-n-weak bg-n-alpha-1 px-3 text-sm text-n-slate-12 placeholder:text-n-slate-10 focus:border-n-brand focus:ring-1 focus:ring-n-brand"
          :placeholder="$t('ASSISTANT.PICTURES.DESCRIPTION_PLACEHOLDER')"
        />
      </label>
      <p class="text-xs text-n-slate-11">
        {{ $t('ASSISTANT.PICTURES.DESCRIPTION_HINT') }}
      </p>
      <div class="flex min-w-0 flex-wrap items-center gap-3">
        <input
          ref="fileInput"
          type="file"
          accept="image/png,image/jpeg,image/webp,image/gif"
          :aria-label="t('ASSISTANT.KNOWLEDGE.UPLOAD_FILE')"
          class="min-h-11 w-full min-w-0 flex-1 rounded-md text-sm text-n-slate-11 file:me-3 file:min-h-11 file:rounded-md file:border-0 file:bg-n-alpha-2 file:px-3 file:text-sm file:font-medium file:text-n-slate-12 focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-brand"
          @change="onFileChange"
        />
        <Button
          :label="$t('ASSISTANT.PICTURES.ADD')"
          icon="i-lucide-plus"
          size="sm"
          class="min-h-11"
          :is-loading="isUploading"
          :disabled="isUploading || !file || !description.trim()"
          @click="upload"
        />
      </div>
    </div>

    <p v-if="isFetching" class="mb-0 p-5 text-sm text-n-slate-11" role="status">
      {{ $t('ASSISTANT.PICTURES.LOADING') }}
    </p>
    <AssistantLoadError
      v-else-if="loadError"
      :framed="false"
      :message="t('ASSISTANT.PICTURES.LOAD_ERROR')"
      @retry="load"
    />
    <p
      v-else-if="!images.length"
      class="mb-0 p-5 text-sm leading-6 text-n-slate-11"
    >
      {{ $t('ASSISTANT.PICTURES.EMPTY') }}
    </p>
    <ul
      v-else
      class="mb-0 grid min-w-0 grid-cols-1 gap-4 p-4 sm:grid-cols-2 sm:p-5 xl:grid-cols-3"
    >
      <li
        v-for="image in images"
        :key="image.id"
        class="flex min-w-0 flex-col overflow-hidden rounded-md border border-n-weak"
      >
        <img
          :src="image.url"
          :alt="image.description"
          loading="lazy"
          class="aspect-video w-full border-b border-n-weak bg-n-alpha-1 object-contain"
        />
        <div class="flex min-w-0 flex-1 flex-col items-start gap-3 p-3">
          <p class="mb-0 flex-1 break-words text-sm leading-6 text-n-slate-12">
            {{ image.description }}
          </p>
          <Button
            ghost
            ruby
            size="xs"
            icon="i-lucide-trash"
            :label="$t('ASSISTANT.PICTURES.DELETE')"
            :aria-label="`${t('ASSISTANT.PICTURES.DELETE')}: ${image.description}`"
            class="min-h-11"
            :is-loading="deletingId === image.id"
            :disabled="deletingId !== null"
            @click="remove(image)"
          />
        </div>
      </li>
    </ul>
  </AssistantSection>
</template>
